import { Nullable } from '@/scripts/models/utility-types';
import { AnalyticPayload } from '../types';
import analytic from '..';
import { processAsUiEvent, UiEventPayload } from './dom-data-processor';

type DomTrackerCallback = (payload: Nullable<AnalyticPayload | UiEventPayload>) => void;

export const getEventComposedPath = (event: Event): EventTarget[] => {
  if ('composedPath' in event) {
    return event.composedPath();
  }

  let target = (event as Event).target as Node;
  const path = [];
  while (target.parentNode !== null) {
    path.push(target);
    target = target.parentNode;
  }

  path.push(document, window);
  return path;
};

export const findTrackable = (event: Event): Nullable<HTMLElement> => {
  const path = getEventComposedPath(event);
  return path.find((element) => {
    if (!('dataset' in element)) return false;

    const dataset = (element as HTMLElement).dataset;
    return ('eventCategory' in dataset) || ('eventType' in dataset);
  }) as Nullable<HTMLElement>;
};

export const trackDomDataEvent = (callback: DomTrackerCallback) => (event: Event): void => {
  const trackable = findTrackable(event);
  if (!trackable) {
    return callback(null);
  }

  if (trackable.dataset.eventType === 'ui_event') {
    callback(
      processAsUiEvent(trackable, { ...trackable.dataset }),
    );
    return;
  }

  const payload = {
    category: trackable.dataset.eventCategory as string,
    action: trackable.dataset.eventAction as string,
    label: trackable.dataset.eventLabel,
    value: trackable.dataset.eventValue ? parseFloat(trackable.dataset.eventValue) : undefined,
    property: trackable.dataset.eventProperty,
  };
  callback(payload);
};

export const initializeSnowplowAutoTracker = (target = window): void => {
  target.addEventListener('click', trackDomDataEvent((payload) => {
    if (!payload) return;

    if ('type' in payload && payload.type === 'ui_event') {
      analytic.trackUiEvent(payload.data);
      return;
    }

    analytic.send({
      ...payload,
      customProperties: {
        ga: false,
      },
    });
  }), {
    capture: true,
  });
};

export const initializeAutoTracker = (): void => {
  import('autotrack');
  initializeSnowplowAutoTracker(window);
};
